/**
 * One-shot request warming (ortex/ortex-gui#1350).
 *
 * A page can start the requests the next page will make on mount (`warm`), and that page's first
 * call for the same key takes the warmed promise (`takeWarm`) instead of sending the request again,
 * whether the warmed request has finished or is still in flight. Each warmed answer is used once and
 * then forgotten, so a later mount, a range change or a refresh always asks the API again: this is
 * not a cache of the data, only a way to start a request early. An answer nobody took within
 * `WARM_TTL_MS` is dropped, and a failed warm-up is dropped at once so the page asks again.
 *
 * Consumers get their own deep copy, because the tab's code mutates the objects it receives.
 */

export const WARM_TTL_MS = 5 * 60 * 1000;

interface Entry {
    at: number;
    promise: Promise<unknown>;
}

const warmed = new Map<string, Entry>();

const fresh = (entry: Entry | undefined, now: number): entry is Entry => !!entry && now - entry.at < WARM_TTL_MS;

const copy = <T>(value: T): T => (value === null || typeof value !== "object" ? value : typeof structuredClone === "function" ? structuredClone(value) : (JSON.parse(JSON.stringify(value)) as T));

/** Start `load` now, keyed by `key`, unless a fresh warm-up for the key is already there. Resolves with the answer (not a copy: read only). */
export const warm = <T>(key: string, load: () => Promise<T>, now = Date.now()): Promise<T> => {
    const existing = warmed.get(key);
    if (fresh(existing, now)) return existing.promise as Promise<T>;
    const promise = load();
    const entry: Entry = { at: now, promise };
    warmed.set(key, entry);
    const drop = () => {
        if (warmed.get(key) === entry) warmed.delete(key);
    };
    promise.catch(drop);
    // An answer nobody takes is dropped after the TTL, so a long session does not keep every
    // overview's warmed series in memory.
    const timer = setTimeout(drop, WARM_TTL_MS) as unknown as { unref?: () => void };
    timer.unref?.(); // Node (tests): do not hold the process open
    return promise;
};

/** The warmed answer for `key` (used once, as a copy) when there is a fresh one, otherwise `load()`. A failed warm-up falls back to `load()`. */
export const takeWarm = <T>(key: string, load: () => Promise<T>, now = Date.now()): Promise<T> => {
    const entry = warmed.get(key);
    if (entry) warmed.delete(key);
    if (fresh(entry, now)) return (entry.promise as Promise<T>).then(copy, () => load());
    return load();
};

/** For tests. */
export const clearWarmRequests = () => warmed.clear();
export const warmedKeys = () => [...warmed.keys()];
